import React, { useMemo } from "react";
import { useQuery } from "wasp/client/operations";
import {
  getVisibleSections,
  getGlobalConfig,
  getUserMembership,
} from "wasp/client/operations";
import { useAuth } from "wasp/client/auth";
import CommonLayout from "../../../components/layout/CommonLayout";
import { dashboardLayoutConfig } from "./dashboardConfig";
import { useViewMode } from "../../contexts/ViewModeContext";
import { useLocationContext } from "../../contexts/LocationContext";

interface DashboardLayoutProps {
  children: React.ReactNode;
  showViewSwitcher?: boolean;
  activeView?: "cards" | "list" | "table";
  onViewChange?: (view: "cards" | "list" | "table") => void;
  forceSidebarCollapse?: boolean;
  customTopBarContent?: React.ReactNode;
}

export default function DashboardLayout({
  children,
  showViewSwitcher = false,
  activeView = "cards",
  onViewChange,
  forceSidebarCollapse = false,
  customTopBarContent,
}: DashboardLayoutProps) {
  const { data: user } = useAuth();
  const { viewMode, portalPack } = useViewMode();
  const { selectedLocation } = useLocationContext();
  const { data: globalConfig } = useQuery(getGlobalConfig);
  const { data } = useQuery(getVisibleSections, {
    locationId: selectedLocation?.id ?? null,
  });
  const { data: membership } = useQuery(
    getUserMembership,
    { locationId: selectedLocation?.id || "" },
    { enabled: !!user && !!selectedLocation?.id },
  );
  const visibilityData = data as
    | { visibleByKey?: Record<string, boolean> }
    | undefined;
  const visibleByKey = visibilityData?.visibleByKey ?? {};
  const labelOverrides = portalPack.sectionLabelOverrides || {};
  const mem = membership as { role?: string } | null | undefined;
  const isBuildingManager =
    !!user?.isAdmin ||
    (!!mem?.role && ["ADMIN", "MANAGER"].includes(mem.role));

  const modifiedConfig = useMemo(() => {
    const filteredSections = dashboardLayoutConfig.menuSections
      .filter((section) => {
        // Always show unlabeled sections (About / Notices / Events)
        if (!section.label) return true;
        const modes = section.viewModes;
        if (!modes || modes.length === 0) return true;
        // Home shows only always-on sections
        if (viewMode === "home") return false;
        return modes.includes(viewMode);
      })
      .map((section) => ({
        ...section,
        label: labelOverrides[section.label] || section.label,
        items: section.items.filter((item) => {
          if (item.requiresBuildingManager && !isBuildingManager) return false;
          if (!item.sectionKey) return true;
          return visibleByKey[item.sectionKey] !== false;
        }),
      }))
      .filter((section) => section.items.length > 0);
    return {
      ...dashboardLayoutConfig,
      brandName: globalConfig?.brandName || dashboardLayoutConfig.brandName,
      brandSubtitle:
        globalConfig?.brandSubtitle || dashboardLayoutConfig.brandSubtitle,
      menuSections: filteredSections,
      customTopBarContent,
    };
  }, [
    viewMode,
    labelOverrides,
    visibleByKey,
    customTopBarContent,
    globalConfig?.brandName,
    globalConfig?.brandSubtitle,
    isBuildingManager,
  ]);

  return (
    <CommonLayout
      config={modifiedConfig}
      showViewSwitcher={showViewSwitcher}
      activeView={activeView}
      onViewChange={onViewChange}
      forceSidebarCollapse={forceSidebarCollapse}
    >
      {children}
    </CommonLayout>
  );
}
